/**
 * admin/ImagePicker.js — choose a picture for a workshop or a pathway.
 *
 * Three ways in, because there are three places pictures actually come
 * from:
 *   Upload    a file from the machine, stored by the backend
 *   Library   something uploaded before, so the same photo isn't
 *             uploaded four times for four retreats
 *   Path      a file that already ships with the website, like
 *             /events/workshop1.jpg — those live in the site's own repo
 *             and the backend has no way to list them
 *
 * The value is always just a string path, which is what the column holds.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { HiOutlineArrowUpTray, HiOutlinePhoto, HiOutlineLink, HiOutlineXMark, HiOutlineTrash } from "react-icons/hi2";
import toast from "react-hot-toast";

import { mediaAPI, mediaUrl } from "../services/api";
import "./ImagePicker.css";

const TABS = [
  { id: "upload", label: "Upload", Icon: HiOutlineArrowUpTray },
  { id: "library", label: "Library", Icon: HiOutlinePhoto },
  { id: "path", label: "Path", Icon: HiOutlineLink },
];

export default function ImagePicker({ value, onChange, label = "Picture", hint = null }) {
  const [tab, setTab] = useState("upload");
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mediaAPI.list();
      setLibrary(res.data || []);
    } catch (err) {
      toast.error(err.message || "Couldn't load the picture library.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (tab === "library") loadLibrary(); }, [tab, loadLibrary]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await mediaAPI.upload(fd);
      onChange(res.data.path);
      toast.success("Picture uploaded.");
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";   // so the same file can be picked again
    }
  };

  const removeFromLibrary = async (item, e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await mediaAPI.remove(item.name);
      if (value === item.path) onChange("");
      await loadLibrary();
    } catch (err) {
      toast.error(err.message || "Couldn't delete that.");
    } finally { setBusy(false); }
  };

  return (
    <div className="aip">
      <label className="aset__label aip__label">{label}</label>

      {value ? (
        <div className="aip__current">
          <img src={mediaUrl(value)} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
          <div className="aip__current-meta">
            <span className="aip__current-path">{value}</span>
            <button type="button" className="aip__clear" onClick={() => onChange("")}>
              <HiOutlineXMark /> Remove
            </button>
          </div>
        </div>
      ) : (
        <p className="aip__empty">No picture yet — the card will use a default.</p>
      )}

      <div className="aip__tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`aip__tab ${tab === t.id ? "is-on" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <t.Icon /> {t.label}
          </button>
        ))}
      </div>

      {tab === "upload" && (
        <div className="aip__pane">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            className="aip__file"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <p className="aip__hint">JPG, PNG, WEBP or AVIF, up to 8 MB.</p>
        </div>
      )}

      {tab === "library" && (
        <div className="aip__pane">
          {loading ? (
            <p className="aip__hint">Loading…</p>
          ) : library.length === 0 ? (
            <p className="aip__hint">Nothing uploaded yet. Use the Upload tab first.</p>
          ) : (
            <div className="aip__grid">
              {library.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  className={`aip__thumb ${value === item.path ? "is-picked" : ""}`}
                  onClick={() => onChange(item.path)}
                  disabled={busy}
                >
                  <img src={mediaUrl(item.path)} alt="" />
                  <span
                    className="aip__thumb-del"
                    role="button"
                    tabIndex={-1}
                    title="Delete this picture"
                    onClick={(e) => removeFromLibrary(item, e)}
                  >
                    <HiOutlineTrash />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "path" && (
        <div className="aip__pane">
          <input
            className="arb__form-input"
            value={value || ""}
            placeholder="/events/workshop1.jpg"
            onChange={(e) => onChange(e.target.value)}
          />
          <p className="aip__hint">
            A file that ships with the website, in its public folder.
          </p>
        </div>
      )}

      {hint && <p className="aip__hint">{hint}</p>}
    </div>
  );
}
